import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import router from '@/router'
import { useAppStore } from '@/stores/app'

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'

interface UserProfile {
  id?: number
  first_name: string
  last_name: string
  user_name: string
  has_password?: boolean
  email: string | null
  avatar_url: string | null
}

interface SignupPayload {
  first_name: string
  last_name: string
  user_name: string
  email?: string
  password: string
  signup_key: 'USER_NAME' | 'EMAIL'
  security_question?: string
  security_answer?: string
}

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(localStorage.getItem('access_token'))
  const user = ref<UserProfile | null>(JSON.parse(localStorage.getItem('user') || 'null'))

  const isAuthenticated = computed(() => !!accessToken.value)

  const displayName = computed(() => {
    if (!user.value) return ''
    return `${user.value.first_name} ${user.value.last_name}`.trim()
  })

  function setTokens(token: string, refreshToken?: string) {
    accessToken.value = token
    localStorage.setItem('access_token', token)
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken)
    window.dispatchEvent(new CustomEvent('ja:auth', { detail: { token } }))
  }

  function setUser(userData: UserProfile | null) {
    user.value = userData
    localStorage.setItem('user', JSON.stringify(userData))
  }

  function clearAuth() {
    accessToken.value = null
    user.value = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
    window.dispatchEvent(new CustomEvent('ja:auth', { detail: { token: null } }))
    useAppStore().resetTheme()
  }

  async function fetchMe(): Promise<void> {
    if (!accessToken.value) return

    try {
      let response = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken.value}` }
      })

      if (response.status === 401) {
        // Access token expired — attempt one silent refresh
        const storedRefresh = localStorage.getItem('refresh_token')
        if (!storedRefresh) {
          clearAuth()
          router.replace('/login')
          return
        }
        try {
          const refreshRes = await fetch(`${API_BASE}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: storedRefresh })
          })
          if (!refreshRes.ok) {
            // Refresh token rejected — genuine session expiry
            clearAuth()
            router.replace('/login')
            return
          }
          const refreshData = await refreshRes.json()
          if (refreshData.access_token) {
            setTokens(refreshData.access_token)
            response = await fetch(`${API_BASE}/auth/me`, {
              headers: { Authorization: `Bearer ${refreshData.access_token}` }
            })
          }
        } catch {
          // Network error during refresh — stay logged in, retry later
          return
        }
      }

      if (response.ok) {
        const data = await response.json()
        setUser({
          id: data.id,
          first_name: data.first_name,
          last_name: data.last_name,
          user_name: data.user_name,
          has_password: data.has_password,
          email: data.email ?? null,
          avatar_url: data.avatar_url ?? null
        })
        await useAppStore().syncSettingsFromServer()
      } else if (response.status === 401) {
        // Still 401 after refresh — session is truly expired
        clearAuth()
        router.replace('/login')
      }
      // 5xx or other server errors: don't clear auth — stay logged in
    } catch {
      // Network unreachable — don't wipe the session
    }
  }

  async function login(
    username: string,
    password: string
  ): Promise<{ ok: boolean; error?: string }> {
    const body = new URLSearchParams()
    body.append('username', username)
    body.append('password', password)

    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
      credentials: 'include'
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      return { ok: false, error: err.detail || 'Login failed' }
    }

    const data = await response.json()
    setTokens(data.access_token, data.refresh_token)
    await fetchMe()
    return { ok: true }
  }

  async function signup(payload: SignupPayload): Promise<{ ok: boolean; error?: string }> {
    const response = await fetch(`${API_BASE}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      return { ok: false, error: err.detail || 'Signup failed' }
    }

    return { ok: true }
  }

  async function checkUsernameAvailability(userName: string): Promise<boolean | null> {
    try {
      const response = await fetch(`${API_BASE}/users/check-user-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_name: userName })
      })
      if (!response.ok) return null
      const data = await response.json()
      return data.is_available as boolean
    } catch {
      return null
    }
  }

  async function getSecurityQuestions(): Promise<string[]> {
    try {
      const response = await fetch(`${API_BASE}/users/security-questions`)
      if (!response.ok) return []
      return await response.json()
    } catch {
      return []
    }
  }
  type ResetMechanismResult =
    | { ok: true; mechanism: 'otp' }
    | { ok: true; mechanism: 'security_question'; securityQuestion: string }
    | { ok: false; error: string }

  async function getResetMechanism(identifier: string): Promise<ResetMechanismResult> {
    const query = new URLSearchParams({ user_identifier: identifier })
    const response = await fetch(`${API_BASE}/auth/get-reset-mechanism?${query.toString()}`)

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      return {
        ok: false,
        error: err.detail || 'We could not find an account matching that username or email'
      }
    }

    const data = await response.json()
    if (data.mechanism === 'security_question') {
      return {
        ok: true,
        mechanism: 'security_question',
        securityQuestion: data.context?.security_question
      }
    }
    return { ok: true, mechanism: 'otp' }
  }

  async function requestResetOtp(identifier: string): Promise<{ ok: boolean; error?: string }> {
    const query = new URLSearchParams({ user_identifier: identifier })
    const response = await fetch(`${API_BASE}/auth/request-reset-otp?${query.toString()}`)

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      return { ok: false, error: err.detail || 'Failed to send code' }
    }

    return { ok: true }
  }

  async function verifyResetMechanism(
    identifier: string,
    mechanism: 'otp' | 'security_question',
    question: string,
    answer: string
  ): Promise<{ ok: boolean; error?: string; token?: string }> {
    const response = await fetch(`${API_BASE}/auth/verify-reset-mechanism`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_identifier: identifier, mechanism, question, answer })
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      return { ok: false, error: err.detail || 'Verification failed' }
    }

    const data = await response.json()
    return data.is_valid
      ? { ok: true, token: data.token }
      : { ok: false, error: mechanism === 'otp' ? 'Incorrect or expired code' : 'Incorrect answer' }
  }

  async function resetPassword(
    token: string,
    newPassword: string
  ): Promise<{ ok: boolean; error?: string }> {
    const response = await fetch(`${API_BASE}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword })
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      return { ok: false, error: err.detail || 'Password reset failed' }
    }

    return { ok: true }
  }

  async function logout() {
    clearAuth()
    router.replace('/login')
  }

  function getAuthHeaders(): Record<string, string> {
    if (!accessToken.value) return {}
    return { Authorization: `Bearer ${accessToken.value}` }
  }

  return {
    accessToken,
    user,
    isAuthenticated,
    displayName,
    login,
    signup,
    logout,
    fetchMe,
    checkUsernameAvailability,
    getSecurityQuestions,
    getResetMechanism,
    requestResetOtp,
    verifyResetMechanism,
    resetPassword,
    setTokens,
    setUser,
    getAuthHeaders,
    clearAuth
  }
})
